import React from "react";
import { FaQuestionCircle } from "react-icons/fa";
import { Tooltip } from "react-tooltip";
import PaginationComponent from "components/common/Pagination/index";
import TimesheetTableCheckbox from "pages/Rostering/UpdatedTimeSheet/components/TimesheetRevamp/TimesheetTableCheckbox";
import { capitalizeAllLetters, classNames } from "Utils/utils";
import {
  CARE_WATCH_VIEWS,
  PAGE_SIZE_OPTIONS,
  READINESS_COLUMN_HELP,
  isCareWatchLiveView,
  liveShiftTimeLabel,
} from "../utils/constants";
import { documentationIssueOverflow } from "../utils/queueUtils";
import CareWatchOverflowCount from "./CareWatchOverflowCount";
import CareWatchParticipantCell from "./CareWatchParticipantCell";
import CareWatchReadinessCell from "./CareWatchReadinessCell";
import CareWatchRiskBadge, { CareWatchRiskControl } from "./CareWatchRiskBadge";
import CareWatchLiveStatusBadge from "./CareWatchLiveStatusBadge";
import CareWatchUnscheduledBadge from "./CareWatchUnscheduledBadge";
import { CareWatchEmptyQueue } from "./CareWatchEmptyStates";

const categoryLabelsOf = (row) => {
  if (Array.isArray(row.categoryLabels) && row.categoryLabels.length) {
    return row.categoryLabels;
  }
  if (row.categoryLabel) return [row.categoryLabel];
  return [];
};

const IssueLabels = ({ row }) => {
  const { visible, overflow } = documentationIssueOverflow(row);
  if (!visible) {
    return <CareWatchRiskBadge mode="issue" />;
  }
  return (
    <span className="inline-flex flex-nowrap items-center gap-1">
      <CareWatchRiskBadge mode="issue" label={visible} />
      <CareWatchOverflowCount
        labels={overflow}
        tooltipId={`cw-issues-${row.id || row.timesheetId}`}
      />
    </span>
  );
};

const CategoryLabels = ({ row }) => {
  const labels = categoryLabelsOf(row);
  const text = labels.length ? labels.join(", ") : "Others";
  const tooltipId = `cw-cat-${row.id || row.timesheetId}`;
  return (
    <>
      <span
        className="care-watch-category"
        data-tooltip-id={tooltipId}
        data-tooltip-content={text}
      >
        {text}
      </span>
      <Tooltip
        id={tooltipId}
        place="top"
        variant="dark"
        className="z-[501] !max-w-xs !text-xs !leading-relaxed"
        positionStrategy="fixed"
      />
    </>
  );
};

const workerNameOf = (row) =>
  capitalizeAllLetters(
    typeof row.worker === "string" ? row.worker : row.worker?.name || ""
  );

const ShiftWhenCell = ({ dateLabel, timeLabel }) => (
  <div
    className="care-watch-shift-when"
    title={[dateLabel, timeLabel].filter(Boolean).join(" · ") || undefined}
  >
    <p className="care-watch-shift-when__date">{dateLabel || "—"}</p>
    {timeLabel ? (
      <p className="care-watch-shift-when__time">{timeLabel}</p>
    ) : null}
  </div>
);

const TableColumns = ({ isLive, isDoc }) => (
  <colgroup>
    {!isLive ? <col className="care-watch-col-check" /> : null}
    {isLive ? (
      <>
        <col className="care-watch-col-live-status" />
        <col className="care-watch-col-worker" />
        <col className="care-watch-col-participant" />
        <col className="care-watch-col-date" />
        <col className="care-watch-col-readiness" />
      </>
    ) : isDoc ? (
      <>
        <col className="care-watch-col-issue" />
        <col className="care-watch-col-participant" />
        <col className="care-watch-col-worker" />
        <col className="care-watch-col-date" />
        <col className="care-watch-col-readiness" />
      </>
    ) : (
      <>
        <col className="care-watch-col-risk" />
        <col className="care-watch-col-participant" />
        <col className="care-watch-col-worker" />
        <col className="care-watch-col-date" />
        <col className="care-watch-col-readiness" />
        <col className="care-watch-col-category" />
      </>
    )}
  </colgroup>
);

export { TableColumns as CareWatchTableColgroup };

const ReportChips = ({ row, isDoc }) => {
  return (
    <>
      {row.status === "acknowledged" && (
        <span className="care-watch-report-chip care-watch-report-chip--acknowledged">
          Acknowledged
        </span>
      )}
      {!isDoc && (
        <>
          {row.reportStatus === "reported" && (
            <span className="care-watch-report-chip care-watch-report-chip--done">
              Reported
            </span>
          )}
          {row.reportStatus === "possibly_reported" && (
            <span
              className="care-watch-report-chip care-watch-report-chip--maybe"
              title="An incident form may already exist for this shift — same participant or worker, same time."
            >
              Possibly reported
            </span>
          )}
          {row.promoteStarted && (
            <span
              className="care-watch-report-chip care-watch-report-chip--started"
              title="A manager opened the incident form but has not filed it yet."
            >
              Form opened
            </span>
          )}
        </>
      )}
    </>
  );
};

const CareWatchTable = ({
  view,
  rows,
  selectedId,
  onSelectRow,
  onToggleChecked,
  onChangeRisk,
  riskSavingId = null,
  page,
  totalPages,
  total,
  pageSize,
  onPageChange,
  onPageSizeChange,
  matchCaption,
}) => {
  const isDoc = view === CARE_WATCH_VIEWS.DOCUMENTATION;
  const isLive = isCareWatchLiveView(view);

  return (
    <div
      className={classNames(
        "care-watch-card care-watch-table",
        isDoc && "care-watch-table--doc",
        isLive && "care-watch-table--live"
      )}
    >
      <div className="care-watch-table-head hidden md:block">
        <table className="min-w-full table-fixed">
          <TableColumns isLive={isLive} isDoc={isDoc} />
          <thead>
            <tr>
              {!isLive ? <th aria-hidden /> : null}
              {isLive ? (
                <>
                  <th>Status</th>
                  <th>Worker</th>
                  <th>Participant</th>
                  <th>Shift window</th>
                  <th>Clock-in</th>
                </>
              ) : (
                <>
                  <th>{isDoc ? "Issue" : "Risk"}</th>
                  <th>Participant</th>
                  <th>Worker</th>
                  <th>Shift date</th>
                  <th className="care-watch-th-help">
                    <span className="care-watch-th-help-label">
                      Readiness
                      <button
                        type="button"
                        data-tooltip-id="care-watch-readiness-help"
                        className="care-watch-th-help-btn"
                        aria-label="What readiness means"
                      >
                        <FaQuestionCircle />
                      </button>
                    </span>
                  </th>
                  {!isDoc ? <th>Category</th> : null}
                </>
              )}
            </tr>
          </thead>
        </table>
      </div>
      <div className="care-watch-table-scroll">
      <div className="hidden md:block">
        <table className="min-w-full table-fixed">
          <TableColumns isLive={isLive} isDoc={isDoc} />
          <tbody>
            {rows.map((row) => {
              const selected = row.id === selectedId;
              const worker = workerNameOf(row);
              return (
                <tr
                  key={row.id}
                  onClick={isLive ? undefined : () => onSelectRow(row.id)}
                  className={classNames(
                    "border-b border-[#F1F5F9] text-[13px] last:border-b-0",
                    !isLive && "cursor-pointer",
                    selected
                      ? "care-watch-row-active"
                      : isLive
                        ? "bg-white"
                        : "bg-white hover:bg-[#F8FAFC]"
                  )}
                >
                  {!isLive ? (
                    <td
                      className="px-2.5 align-middle"
                      onClick={(event) => event.stopPropagation()}
                    >
                      <span className="flex justify-center">
                        <TimesheetTableCheckbox
                          checked={selected}
                          onChange={() => onToggleChecked(row.id)}
                        />
                      </span>
                    </td>
                  ) : null}
                  {isLive ? (
                    <>
                      <td className="px-2.5 align-middle">
                        <span className="inline-flex flex-wrap items-center gap-1.5">
                          <CareWatchLiveStatusBadge state={row.liveState} />
                          <CareWatchUnscheduledBadge row={row} />
                        </span>
                      </td>
                      <td className="px-2.5 align-middle">
                        <p
                          className="truncate text-[13px] font-medium text-[#111827]"
                          title={worker || undefined}
                        >
                          {worker || "—"}
                        </p>
                      </td>
                      <td className="px-2.5 align-middle">
                        <CareWatchParticipantCell participants={row.participants} />
                      </td>
                      <td className="px-2.5 align-middle">
                        <ShiftWhenCell
                          dateLabel={row.shiftDateLabel}
                          timeLabel={isLive ? liveShiftTimeLabel(row) : row.shiftTimeLabel}
                        />
                      </td>
                      <td className="px-2.5 align-middle text-[13px] font-medium text-[#111827]">
                        {row.clockInLabel || "—"}
                      </td>
                    </>
                  ) : (
                    <>
                      <td className="px-2.5 align-middle">
                        {isDoc ? (
                          <IssueLabels row={row} />
                        ) : !onChangeRisk ? (
                          <CareWatchRiskBadge risk={row.risk} />
                        ) : (
                          <CareWatchRiskControl
                            risk={row.risk}
                            disabled={riskSavingId === row.id}
                            onChange={(risk) => onChangeRisk(row, risk)}
                          />
                        )}
                      </td>
                      <td className="px-2.5 align-middle">
                        <CareWatchParticipantCell participants={row.participants} />
                      </td>
                      <td className="px-2.5 align-middle">
                        <p
                          className="truncate text-[13px] font-medium text-[#111827]"
                          title={worker || undefined}
                        >
                          {worker || "—"}
                        </p>
                      </td>
                      <td className="px-2.5 align-middle">
                        <ShiftWhenCell
                          dateLabel={row.shiftDateLabel}
                          timeLabel={isLive ? liveShiftTimeLabel(row) : row.shiftTimeLabel}
                        />
                      </td>
                      <td className="px-2.5 align-middle">
                        <CareWatchReadinessCell score={row.readinessScore} />
                      </td>
                      {!isDoc ? (
                        <td className="care-watch-td-category px-2.5 align-middle">
                          <CategoryLabels row={row} />
                        </td>
                      ) : null}
                    </>
                  )}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      <div className="flex flex-col md:hidden">
        {rows.map((row) => {
          const selected = row.id === selectedId;
          const worker = workerNameOf(row);
          return (
            <div
              key={row.id}
              role={isLive ? undefined : "button"}
              tabIndex={isLive ? undefined : 0}
              onClick={isLive ? undefined : () => onSelectRow(row.id)}
              onKeyDown={
                isLive
                  ? undefined
                  : (event) => {
                      if (event.key === "Enter" || event.key === " ") {
                        event.preventDefault();
                        onSelectRow(row.id);
                      }
                    }
              }
              className={classNames(
                "border-b border-[#F1F5F9] px-3 py-3 text-left last:border-b-0",
                selected
                  ? "care-watch-row-active shadow-[inset_4px_0_0_#4B6CB7]"
                  : "bg-white"
              )}
            >
              <div className="flex items-start gap-3">
                {!isLive ? (
                  <span
                    onClick={(event) => event.stopPropagation()}
                    className="pt-1"
                  >
                    <TimesheetTableCheckbox
                      checked={selected}
                      onChange={() => onToggleChecked(row.id)}
                    />
                  </span>
                ) : null}
                <span className="min-w-0 flex-1">
                  <span className="mb-2 flex flex-wrap items-center gap-2">
                    {isLive ? (
                      <>
                        <CareWatchLiveStatusBadge state={row.liveState} />
                        <CareWatchUnscheduledBadge row={row} />
                      </>
                    ) : isDoc ? (
                      <IssueLabels row={row} />
                    ) : !onChangeRisk ? (
                      <CareWatchRiskBadge risk={row.risk} />
                    ) : (
                      <CareWatchRiskControl
                        risk={row.risk}
                        disabled={riskSavingId === row.id}
                        onChange={(risk) => onChangeRisk(row, risk)}
                      />
                    )}
                    {!isDoc && !isLive && <CategoryLabels row={row} />}
                    {!isLive ? <ReportChips row={row} isDoc={isDoc} /> : null}
                  </span>
                  {isLive ? (
                    <p className="truncate text-[13px] font-semibold text-[#111827]">
                      {worker || "—"}
                    </p>
                  ) : (
                    <CareWatchParticipantCell participants={row.participants} />
                  )}
                  <div className="mt-1.5">
                    {isLive ? (
                      <CareWatchParticipantCell participants={row.participants} />
                    ) : null}
                    <ShiftWhenCell
                      dateLabel={row.shiftDateLabel}
                      timeLabel={isLive ? liveShiftTimeLabel(row) : row.shiftTimeLabel}
                    />
                    {worker && !isLive ? (
                      <p className="mt-0.5 truncate text-[12px] text-[#6B7280]">
                        {worker}
                      </p>
                    ) : null}
                    {isLive && row.clockInLabel ? (
                      <p className="mt-0.5 truncate text-[12px] text-[#6B7280]">
                        Clock-in {row.clockInLabel}
                      </p>
                    ) : null}
                  </div>
                  {!isLive ? (
                    <div className="mt-2 max-w-[220px]">
                      <CareWatchReadinessCell
                        score={row.readinessScore}
                        compact
                      />
                    </div>
                  ) : null}
                </span>
              </div>
            </div>
          );
        })}
      </div>

      {rows.length === 0 && (
        <div className="flex min-h-full flex-1 flex-col">
          <CareWatchEmptyQueue variant={isLive ? "live" : "signals"} />
        </div>
      )}
      </div>
      {rows.length > 0 && (
        <p className="care-watch-table-status flex items-center justify-center bg-[#F6F7FB] px-4 py-2.5 text-xs font-medium text-[#4B5563]">
          {matchCaption ||
            (isLive
              ? `${total} live shift${total === 1 ? "" : "s"} in this period`
              : `${total} signal${total === 1 ? "" : "s"} in this period  ·  You’re up to date`)}
        </p>
      )}
      {/* Live is a single floor list — no pagination needed. */}
      {!isLive ? (
        <div className="care-watch-table-pager">
          <PaginationComponent
            variant="compact"
            page={page}
            totalPages={totalPages}
            totalList={total}
            limit={pageSize}
            activeListLength={rows}
            handlePagination={onPageChange}
            showPerPageDropdown
            perPageData={PAGE_SIZE_OPTIONS.map((value) => ({
              label: `${value} / page`,
              value,
            }))}
            perPageValue={{
              label: `${pageSize} / page`,
              value: pageSize,
            }}
            handleLimitChange={(option) =>
              onPageSizeChange(Number(option?.value ?? option))
            }
          />
        </div>
      ) : null}
      {!isLive ? (
        <Tooltip
          id="care-watch-readiness-help"
          place="top"
          variant="dark"
          positionStrategy="fixed"
          className="care-watch-readiness-tooltip"
          content={READINESS_COLUMN_HELP}
        />
      ) : null}
    </div>
  );
};

export default CareWatchTable;
